/**
 * Analytics event listeners setup
 * Tracks all user interactions across the portfolio
 */

import { analytics } from './analytics.js';

export class AnalyticsEvents {
  constructor() {
    this.scrollDepthTracked = new Set();
    this.init();
  }

  init() {
    this.trackNavigation();
    this.trackProjectInteractions();
    this.trackCVDownload();
    this.trackSocialLinks();
    this.trackCertifications();
    this.trackScrollDepth();
    this.trackFilters();
    this.trackFormSubmission();
  }

  /**
   * Track navigation clicks
   */
  trackNavigation() {
    const navLinks = document.querySelectorAll('.nav-links a, .footer-links a');
    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const section = link.getAttribute('href')?.replace('#', '') || 'unknown';
        analytics.trackNavigation(section);
      });
    });
  }

  /**
   * Track project card interactions
   */
  trackProjectInteractions() {
    // Track project card views (when visible in viewport)
    const projectCards = document.querySelectorAll('.project-card');
    const projectObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const projectCard = entry.target;
          const projectName = projectCard.querySelector('h3')?.textContent || 'Unknown';
          const category = projectCard.getAttribute('data-category') || 'unknown';

          analytics.trackProjectView(projectName, category);
          projectObserver.unobserve(entry.target); // Track once
        }
      });
    }, { threshold: 0.5 });

    projectCards.forEach(card => projectObserver.observe(card));

    // Track project link clicks
    const projectLinks = document.querySelectorAll('.project-link, .project-card a');
    projectLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const projectCard = link.closest('.project-card');
        const projectName = projectCard?.querySelector('h3')?.textContent || 'Unknown';

        // Determine link type from URL or icon
        let linkType = 'other';
        const href = link.getAttribute('href') || '';
        if (href.includes('github.com')) linkType = 'github';
        else if (href.includes('demo') || href.includes('live')) linkType = 'demo';
        else if (href.includes('arxiv') || href.includes('paper')) linkType = 'paper';

        analytics.trackProjectClick(projectName, linkType);
      });
    });
  }

  /**
   * Track CV download
   */
  trackCVDownload() {
    const cvLinks = document.querySelectorAll('a[download*="CV"], a[href*="cv.pdf"]');
    cvLinks.forEach(link => {
      link.addEventListener('click', () => {
        analytics.trackCVDownload();
      });
    });
  }

  /**
   * Track social media clicks
   */
  trackSocialLinks() {
    const socialLinks = document.querySelectorAll('.social-links a, .footer-social a, .contact-social a');
    socialLinks.forEach(link => {
      link.addEventListener('click', () => {
        const href = link.getAttribute('href') || '';
        let platform = 'other';

        if (href.includes('github.com')) platform = 'github';
        else if (href.includes('linkedin.com')) platform = 'linkedin';
        else if (href.includes('twitter.com') || href.includes('x.com')) platform = 'twitter';

        analytics.trackSocialClick(platform);
      });
    });
  }

  /**
   * Track certification clicks
   */
  trackCertifications() {
    const certCards = document.querySelectorAll('.certification-card[href]');
    certCards.forEach(card => {
      card.addEventListener('click', () => {
        const certName = card.querySelector('h3')?.textContent || 'Unknown';
        const issuer = card.querySelector('.certification-issuer')?.textContent || 'Unknown';

        analytics.trackCertificationClick(certName, issuer);
      });
    });
  }

  /**
   * Track scroll depth (25%, 50%, 75%, 100%)
   */
  trackScrollDepth() {
    const trackDepth = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      // Track at 25%, 50%, 75%, 100%
      [25, 50, 75, 100].forEach(threshold => {
        if (scrollPercent >= threshold && !this.scrollDepthTracked.has(threshold)) {
          this.scrollDepthTracked.add(threshold);
          analytics.trackScrollDepth(threshold);
        }
      });
    };

    // Throttle scroll events
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          trackDepth();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  /**
   * Track project filter usage
   */
  trackFilters() {
    const filterButtons = document.querySelectorAll('.filter-btn');
    filterButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const filterValue = btn.getAttribute('data-filter') || 'unknown';
        analytics.trackFilterUse('project', filterValue);
      });
    });
  }

  /**
   * Track contact form submission
   */
  trackFormSubmission() {
    const contactForm = document.getElementById('contactForm');
    if (!contactForm) return;

    contactForm.addEventListener('submit', (e) => {
      const subjectInput = contactForm.querySelector('#subject');
      const subject = subjectInput?.value || 'Not specified';

      // Track submission (don't prevent default, form will submit)
      analytics.trackFormSubmission(subject);
    });
  }

  /**
   * Listen to theme changes (if ThemeManager emits events)
   */
  listenToThemeChanges(eventBus) {
    if (!eventBus) return;

    eventBus.on('theme:changed', (theme) => {
      analytics.trackThemeToggle(theme);
    });
  }
}

// Auto-initialize when DOM is ready
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new AnalyticsEvents();
    });
  } else {
    new AnalyticsEvents();
  }
}
